import React from "react";
import { Link } from "gatsby";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Seo from "../../components/Seo";
import TrustpilotInline from "../../components/TrustpilotInline";

import "../../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const supportAreas = [
	[
		"01",
		"Personal care and routines",
		"Support with washing, dressing, continence and daily routines can be adapted around fatigue, mobility changes and the person's preferred pace.",
	],
	[
		"02",
		"Medication and monitoring",
		"Prompts or safe administration can follow the agreed care plan, with changes in symptoms, appetite or mood shared with the wider care team.",
	],
	[
		"03",
		"Mobility and transfers",
		"Care can support walking, repositioning and transfers safely, while following guidance from physiotherapists, occupational therapists and other clinicians.",
	],
	[
		"04",
		"Speech, swallowing and meals",
		"Support can reflect speech and language therapy guidance, with calm mealtimes, appropriate textures and close attention to hydration.",
	],
	[
		"05",
		"Rehabilitation and confidence",
		"Encouragement to practise agreed exercises, rebuild confidence and keep doing meaningful activities without taking over what the person can still do.",
	],
	[
		"06",
		"Reassurance for family",
		"One-to-one support at home can reduce avoidable stress and help families stay informed about recovery, risks and progress.",
	],
];

const signs = [
	"Face weakness or drooping",
	"Arm weakness or numbness",
	"Speech that is slurred or hard to understand",
	"Confusion or difficulty understanding",
	"Sudden problems with balance or coordination",
	"Sudden changes to vision, strength or sensation",
];

const strokeTypes = [
	[
		"Ischaemic stroke",
		"The most common type of stroke. It happens when a blockage interrupts blood flow to the brain and can lead to weakness, speech changes or problems with thinking and coordination.",
	],
	[
		"Haemorrhagic stroke",
		"This happens when a blood vessel in or around the brain bursts. Recovery needs can be complex and may change over time depending on the area affected.",
	],
	[
		"Transient ischaemic attack (TIA)",
		"Sometimes called a mini-stroke, a TIA is a temporary interruption to the blood supply to the brain. Symptoms may pass quickly, but urgent medical advice is still essential.",
	],
];

const homePrinciples = [
	[
		"Create clear walking routes",
		"Reduce trip hazards, improve lighting and keep key rooms easy to move through with any mobility aid or transfer support in mind.",
	],
	[
		"Make rehabilitation practical",
		"Agree where exercises, seated routines or assisted movement can happen safely and comfortably as part of the day.",
	],
	[
		"Protect medication and hydration",
		"Keep medication organised and visible, with an easy routine for drinks, meals and any swallowing guidance.",
	],
	[
		"Review as recovery changes",
		"A Care Manager can revisit risks, equipment and support needs as the person regains abilities or develops longer-term needs.",
	],
];

const faqs = [
	[
		"Can someone recover from a stroke at home?",
		"Many people can continue rehabilitation and longer-term recovery at home with the right support. Whether live-in care is appropriate depends on the person's mobility, communication, risks, home environment and clinical guidance.",
	],
	[
		"Can a live-in carer help with rehabilitation exercises?",
		"A carer can support exercises and routines that have been recommended by a physiotherapist, occupational therapist or other qualified professional. They do not design a clinical rehabilitation plan themselves.",
	],
	[
		"How does care work if speech or swallowing has changed?",
		"The care plan can reflect speech and language therapy guidance, including support with communication, mealtimes, texture-modified diets and safe swallowing strategies where these have been prescribed.",
	],
	[
		"Will the same carer understand the person's progress?",
		"Edyn aims to provide continuity wherever possible and the wider care team oversees changes to the plan. Observations about mobility, confidence, fatigue or mood can be shared and reviewed over time.",
	],
	[
		"What happens if recovery is slower than expected?",
		"Stroke recovery is rarely linear. The Care Manager can review needs, update the care plan and help the family think clearly about what support is needed now rather than relying on early assumptions.",
	],
];

export default function StrokeCare() {
	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Stroke Recovery Live-in Care at Home | Edyn Care"
				description="Personalised live-in stroke care supporting rehabilitation, safety and everyday life at home, managed by Edyn's experienced care team."
				path="/condition/stroke/"
				image="https://www.edyn.care/assets/marketing-site/images/condition_stroke_splash.jpeg"
			/>
			<Header />

			<main
				id="main-content"
				className="edyn-dementia edyn-dementia--textured edyn-stroke"
			>
				<section className="dementia-hero">
					<div className="site-shell">
						<div className="dementia-hero__panel">
							<div className="dementia-hero__copy">
								<p className="eyebrow eyebrow--light">Stroke care at home</p>
								<h1>Recovery rarely follows a straight line.</h1>
							</div>
							<div className="dementia-hero__summary">
								<p className="dementia-hero__lead">
									One-to-one care can support rehabilitation, confidence and
									everyday life after a stroke while helping someone remain in
									the home they know best.
								</p>
								<ul>
									<li>Support shaped around the stage of recovery</li>
									<li>A professional carer in the home</li>
									<li>Ongoing oversight from a Care Manager</li>
								</ul>
								<div className="button-row">
									<Link className="button button--cream" to="/care-consultation/">
										Talk to a Care Advisor
									</Link>
									<a className="button button--text-light" href="#stroke-support">
										Explore support <span aria-hidden="true">&#8595;</span>
									</a>
								</div>
							</div>
						</div>

						<div className="dementia-hero__visual">
							<img
								src={`${imagePath}/condition_stroke_splash.jpeg`}
								alt="An older woman and professional carer sharing time together at home"
								width="1920"
								height="1280"
							/>
							<div className="dementia-hero__note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Steady support after hospital.</strong>
									Space for recovery at home.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section className="condition-trustpilot" aria-label="Customer reviews">
					<div className="site-shell condition-trustpilot__inner">
						<TrustpilotInline />
					</div>
				</section>

				<section
					className="dementia-assurance"
					aria-label="Edyn care assurances"
				>
					<div className="site-shell dementia-assurance__grid">
						<p>
							<strong>One-to-one</strong>
							<span>support in the home</span>
						</p>
						<p>
							<strong>Fully managed</strong>
							<span>by an Edyn care team</span>
						</p>
						<p>
							<strong>CQC registered</strong>
							<span>care across England</span>
						</p>
					</div>
				</section>

				<section className="dementia-person section-space">
					<div className="site-shell dementia-person__grid">
						<p className="eyebrow">Life after stroke</p>
						<div>
							<h2>Good care supports recovery without taking over the person.</h2>
							<div className="dementia-person__copy">
								<p>
									Some people recover quickly after a stroke. Others live with
									longer-term changes to movement, speech, swallowing, memory or
									fatigue. Most families experience a mixture of progress,
									setbacks and uncertainty.
								</p>
								<p>
									The right support can make home a strong place for recovery:
									safe, familiar and shaped around the person's own goals rather
									than an institution's routine.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section
					id="stroke-support"
					className="dementia-support section-space section-dark"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow eyebrow--light">Everyday support</p>
								<h2>Practical help that works alongside rehabilitation.</h2>
							</div>
							<p>
								Care follows the agreed plan and can sit alongside support from
								hospital teams, therapists, GPs and community clinicians.
							</p>
						</div>
						<div className="dementia-support__grid">
							{supportAreas.map(([number, title, copy]) => (
								<article key={title}>
									<span>{number}</span>
									<h3>{title}</h3>
									<p>{copy}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-understanding section-space">
					<div className="site-shell dementia-understanding__grid">
						<div className="dementia-understanding__intro">
							<p className="eyebrow">Understanding stroke</p>
							<h2>Act fast, then plan for the longer recovery.</h2>
							<p>
								A stroke happens when the blood supply to part of the brain is
								interrupted. The sooner treatment begins, the better the chance
								of limiting lasting damage.
							</p>
							<a
								className="dementia-source"
								href="https://www.nhs.uk/conditions/stroke/"
								target="_blank"
								rel="noopener noreferrer"
							>
								Read NHS stroke guidance{" "}
								<span aria-hidden="true">&#8599;</span>
							</a>
						</div>
						<ul className="dementia-understanding__signs">
							{signs.map((sign, index) => (
								<li key={sign}>
									<span>{String(index + 1).padStart(2, "0")}</span>
									{sign}
								</li>
							))}
						</ul>
					</div>
				</section>

				<section className="dementia-facts section-space">
					<div className="site-shell dementia-facts__panel">
						<div className="dementia-facts__title">
							<img
								src={`${imagePath}/we-care-image-2.jpg`}
								alt=""
								loading="lazy"
							/>
							<div>
								<p className="eyebrow eyebrow--light">The wider picture</p>
								<h2>Stroke facts and figures</h2>
							</div>
						</div>
						<div className="dementia-facts__grid">
							<p>
								<strong>Someone every 5 minutes</strong>
								<span>has a stroke in the UK</span>
							</p>
							<p>
								<strong>Over 100,000</strong>
								<span>strokes happen in the UK each year</span>
							</p>
							<p>
								<strong>1.2 million</strong>
								<span>stroke survivors are living in the UK</span>
							</p>
							<p>
								<strong>Almost two thirds</strong>
								<span>leave hospital with some level of disability</span>
							</p>
							<p>
								<strong>Twice as likely</strong>
								<span>
									Black people are estimated to experience stroke compared with
									white people
								</span>
							</p>
							<p>
								<strong>£1.7 billion</strong>
								<span>
									is the estimated annual NHS and social care cost of stroke in
									England
								</span>
							</p>
						</div>
					</div>
				</section>

				<section className="dementia-types section-space section-soft">
					<div className="site-shell dementia-types__grid">
						<div>
							<p className="eyebrow">Different types</p>
							<h2>Not every stroke is caused in the same way.</h2>
							<p>
								The type of stroke, the part of the brain affected and how
								quickly treatment began can all shape recovery and longer-term
								support needs.
							</p>
						</div>
						<div className="dementia-types__list">
							{strokeTypes.map(([title, copy], index) => (
								<details key={title} open={index === 0}>
									<summary>
										<span>{String(index + 1).padStart(2, "0")}</span>
										{title}
									</summary>
									<p>{copy}</p>
								</details>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-home section-space">
					<div className="site-shell dementia-home__grid">
						<div className="dementia-home__visual stroke-home__visual">
							<img
								src={`${imagePath}/support-teams.png`}
								alt="A calm, well-supported home environment for ongoing recovery"
								loading="lazy"
							/>
						</div>
						<div className="dementia-home__copy">
							<p className="eyebrow">Preparing the home</p>
							<h2>Small changes can make rehabilitation safer and easier.</h2>
							<p>
								Before care begins, we assess the home and agree practical
								changes that support mobility, confidence and day-to-day
								recovery without making the environment feel clinical.
							</p>
							<div className="dementia-home__principles">
								{homePrinciples.map(([title, copy]) => (
									<article key={title}>
										<h3>{title}</h3>
										<p>{copy}</p>
									</article>
								))}
							</div>
						</div>
					</div>
				</section>

				<section className="dementia-managed section-space section-dark">
					<div className="site-shell">
						<div className="dementia-managed__heading">
							<p className="eyebrow eyebrow--light">
								Fully managed stroke care
							</p>
							<h2>One carer in the home. A wider team behind recovery.</h2>
						</div>
						<div className="dementia-managed__steps">
							<article>
								<span>01</span>
								<h3>Assess</h3>
								<p>
									A Care Manager understands the stroke history, current
									abilities, risks, routines and clinical guidance.
								</p>
							</article>
							<article>
								<span>02</span>
								<h3>Match</h3>
								<p>
									We select a professional carer whose experience, manner and
									approach fit the person and their stage of recovery.
								</p>
							</article>
							<article>
								<span>03</span>
								<h3>Review</h3>
								<p>
									As recovery changes, the wider Edyn team can adjust the care
									plan, review risks and coordinate next steps with the family.
								</p>
							</article>
						</div>
						<Link className="button button--cream" to="/approach/">
							See how Edyn works
						</Link>
					</div>
				</section>

				<section className="dementia-faq section-space">
					<div className="site-shell dementia-faq__grid">
						<div>
							<p className="eyebrow">Questions families ask</p>
							<h2>Clear answers when the path ahead feels uncertain.</h2>
							<p>
								Stroke can change life very quickly. These are some of the
								questions families often ask while planning the next stage.
							</p>
						</div>
						<div className="dementia-faq__list">
							{faqs.map(([question, answer]) => (
								<details key={question}>
									<summary>{question}</summary>
									<p>{answer}</p>
								</details>
							))}
						</div>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	);
}
